import { Box, Divider, Flex, Modal } from '@mantine/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

import type { IProdoqCompanyResponse, IProdoqRoleResponse } from '../../../../../shared/types/prodoq';
import RoleRegFormPanel from './ui/RoleRegFormPanel';
import RoleRegListPanel from './ui/RoleRegListPanel';
import { createRoleRegSchema } from '../utils/roleRegSchema';
import useGetProdoqRoles from '../hooks/useGetProdoqRoles';
import usePostRole from '../hooks/usePostRole';
import usePutRole from '../hooks/usePutRole';
import useDeleteRole from '../hooks/useDeleteRole';

export interface IRoleFormFields {
  roleName: string;
  roleNameEn: string;
  order: string;
}

export default function RoleRegModal(props: {
  opened: boolean;
  onClose: () => void;
  selectedCompany: IProdoqCompanyResponse | null;
}) {
  const { opened, onClose, selectedCompany } = props;
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const companyId = selectedCompany?.companyId ?? 0;

  const { data: roles } = useGetProdoqRoles({ companyId });
  const selectedRole = roles?.find((role) => role.id === selectedRoleId) ?? null;
  const totalCount = roles?.length ?? 0;
  const newOrderNumber = totalCount + 1;
  const roleRegSchema = useMemo(
    () => createRoleRegSchema({ isEditMode: !!selectedRoleId, totalCount }),
    [selectedRoleId, totalCount],
  );

  const { mutate: postRole } = usePostRole();
  const { mutate: putRole } = usePutRole();
  const { mutate: deleteRole } = useDeleteRole();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IRoleFormFields>({
    resolver: yupResolver(roleRegSchema),
    defaultValues: {
      roleName: '',
      roleNameEn: '',
      order: String(newOrderNumber),
    },
  });

  const resetFields = (data: IRoleFormFields) => {
    reset(data);
  };

  const onSubmit = (data: IRoleFormFields) => {
    if (selectedRoleId) {
      putRole(
        {
          companyId,
          roleId: selectedRoleId,
          roleName: data.roleName,
          roleNameEn: data.roleNameEn,
          order: Number(data.order),
        },
        {
          onSuccess: () => {
            onClickReset();
          },
        },
      );
    } else {
      postRole(
        {
          companyId,
          roleName: data.roleName,
          roleNameEn: data.roleNameEn,
          order: Number(data.order),
        },
        {
          onSuccess: () => {
            onClickReset();
          },
        },
      );
    }
  };

  const onClickEdit = (item: IProdoqRoleResponse) => {
    setSelectedRoleId(item.id);
    resetFields({
      roleName: item.roleName,
      roleNameEn: item.roleNameEn,
      order: String(item.order),
    });
  };

  const onClickReset = () => {
    setSelectedRoleId(null);
    resetFields({
      roleName: '',
      roleNameEn: '',
      order: String(newOrderNumber),
    });
  };

  const confirmDelete = (id: number) => {
    Swal.fire({
      title: '정말 삭제하시겠습니까?',
      text: '이미 등록된 하위 계정이 있는 경우 삭제가 불가합니다.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        if (!companyId) return;
        deleteRole({
          companyId,
          roleId: id,
        });
      }
    });
  };

  useEffect(() => {
    if (!opened || selectedRoleId) {
      return;
    }

    resetFields({
      roleName: '',
      roleNameEn: '',
      order: String(newOrderNumber),
    });
  }, [selectedRoleId, newOrderNumber, opened]);

  return (
    <Modal opened={opened} onClose={onClose} title="역할 관리" centered size="xl">
      <Flex>
        <Box flex={1}>
          <RoleRegFormPanel
            companyName={selectedCompany?.name || ''}
            control={control}
            errors={errors}
            handleSubmit={handleSubmit}
            isEditMode={!!selectedRoleId}
            isOrderDisabled={selectedRole?.order === 1}
            onReset={onClickReset}
            onSubmit={onSubmit}
            register={register}
          />
        </Box>
        <Divider orientation="vertical" mx="lg" />
        <RoleRegListPanel roleData={roles || []} onClickEdit={onClickEdit} onConfirmDelete={confirmDelete} />
      </Flex>
    </Modal>
  );
}
